import { motion } from "framer-motion";
import { 
  GraduationCap, 
  Target, 
  Eye, 
  Heart, 
  Users, 
  Award,
  BookOpen,
  Shield,
  Lightbulb,
  Globe
} from "lucide-react";

const values = [
  { icon: Heart, title: "Integrity", description: "Upholding honesty and ethical values in all actions" },
  { icon: Lightbulb, title: "Innovation", description: "Embracing creative approaches to learning" },
  { icon: Users, title: "Collaboration", description: "Fostering teamwork and community spirit" },
  { icon: Shield, title: "Excellence", description: "Striving for the highest standards" },
  { icon: Globe, title: "Global Mindset", description: "Preparing students for a connected world" },
  { icon: BookOpen, title: "Lifelong Learning", description: "Instilling a passion for continuous growth" },
];

const About = () => {
  return (
    <div data-testid="about-page">
      {/* Page Header */}
      <section className="bg-primary py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              About ID4U School
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Discover our journey of excellence in education since 2005
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=800&q=80"
                  alt="School Campus"
                  className="rounded-2xl shadow-xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-secondary text-white p-6 rounded-2xl shadow-lg">
                  <div className="text-4xl font-bold">19+</div>
                  <div className="text-sm">Years of Excellence</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-secondary font-medium text-sm uppercase tracking-wider">
                Our Story
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6">
                Building Future Leaders Since 2005
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                ID4U School was founded in 2005 with a vision to provide quality education 
                that goes beyond textbooks. Our founders believed that every child deserves 
                an opportunity to discover their potential in a nurturing environment.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Over the years, we have grown from a small institution to one of the most 
                respected schools in the region, known for our academic excellence, innovative 
                teaching methods, and holistic approach to education.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Today, we continue to uphold our commitment to excellence, preparing students 
                not just for academic success, but for life. Our alumni have gone on to excel 
                in various fields, carrying forward the values instilled at ID4U School.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <Target className="text-primary" size={32} />
              </div>
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To provide a comprehensive education that nurtures the intellectual, physical, 
                emotional, and social development of every student. We are committed to creating 
                a safe, inclusive, and stimulating learning environment where students can 
                discover their passions, develop critical thinking skills, and become responsible 
                global citizens.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100"
            >
              <div className="w-16 h-16 bg-secondary/10 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="text-secondary" size={32} />
              </div>
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To be a leading educational institution recognized for excellence in academics, 
                character development, and innovation. We envision a school community where every 
                student is empowered to achieve their full potential and contribute positively to 
                society, equipped with the knowledge, skills, and values needed for success in a 
                rapidly changing world.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">
              What We Stand For
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Our Core Values
            </h2>
            <div className="section-divider mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-50 p-6 rounded-xl hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="text-primary" size={24} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { icon: Users, value: "2500+", label: "Students" },
              { icon: GraduationCap, value: "120+", label: "Teachers" },
              { icon: Award, value: "50+", label: "Awards" },
              { icon: BookOpen, value: "98%", label: "Pass Rate" },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="text-secondary" size={32} />
                </div>
                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-white/80">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
